import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useLocation, useNavigate } from "react-router-dom";
import { Footer, Navbar } from "../components";
import axios from "axios";
import configData from "../config.json";
import { format } from "date-fns";

const OrderList = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const email = location.state?.email || localStorage.getItem("userEmail");

    const [orders, setOrderList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!email) {
            navigate("/login");
            return;
        }

        const getOrderList = async () => {
            try {
                const response = await axios.get(
                    `${configData.SERVER_URL}/orderList/${email}`
                );
                setOrderList(response.data || []);
            } catch (err) {
                console.error(err);
                setError("Failed to load orders");
            } finally {
                setLoading(false);
            }
        };

        getOrderList();
    }, [email, navigate]);

    const orderPayment = async (order) => {
        try {
            const response = await axios.get(
                `${configData.SERVER_URL}/orderPayment/${order.OrderID}`
            );

            if (String(response.data.Status) === '200') {
                window.location.assign(response.data.RedirectToGateway);
            } else {
                console.error('Payment request failed:', response.data.Message);
            }
        } catch (err) {
            console.error(err);
            alert("Payment failed. Try again.");
        }
    };

    return (
        <>
            <Navbar />

            <div className="container py-4">
    <div className="text-center mb-5">
        <h2 className="fw-bold">My Orders</h2>
        <p className="text-muted mb-0">
            View your recent orders and payment status
        </p>
    </div>

    {loading ? (
        <Skeleton height={180} count={3} className="mb-3" />
    ) : error ? (
        <div className="alert alert-danger text-center shadow-sm">
            {error}
        </div>
    ) : orders.length === 0 ? (
        <div className="card border-0 shadow-sm text-center p-5">
            <h5>No Orders Found</h5>
            <p className="text-muted mb-0">
                Your order history is empty
            </p>
        </div>
    ) : (
        <div className="row g-4">
            {orders.map((order) => (
                <div className="col-lg-6" key={order.OrderID}>
                    <div
                        className="card border-0 shadow-sm h-100"
                        style={{
                            borderRadius: "16px",
                            overflow: "hidden",
                        }}
                    >
                        {/* HEADER */}
                        <div
                            className="p-3 text-white"
                            style={{
                                background:
                                    "linear-gradient(135deg,#0d6efd,#0dcaf0)",
                            }}
                        >
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h5 className="mb-1 fw-bold">
                                        #{order.OrderID}
                                    </h5>

                                    <small>
                                        {order.OrderDate
                                            ? format(
                                                  new Date(order.OrderDate),
                                                  "dd MMM yyyy"
                                              )
                                            : "N/A"}
                                    </small>
                                </div>

                                <div className="text-end">
                                    <h5 className="mb-0 fw-bold">
                                        ৳
                                        {Number(
                                            order.SubTotal || 0
                                        ).toLocaleString()}
                                    </h5>

                                    <small>Total Amount</small>
                                </div>
                            </div>
                        </div>

                        {/* BODY */}
                        <div className="card-body">
                            <div className="row mb-3">
                                <div className="col-6">
                                    <small className="text-muted">
                                        Customer
                                    </small>
                                    <div className="fw-semibold">
                                        {order.FullName}
                                    </div>
                                </div>

                                <div className="col-6">
                                    <small className="text-muted">
                                        Phone
                                    </small>
                                    <div className="fw-semibold">
                                        {order.Phone}
                                    </div>
                                </div>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted">
                                    Delivery Address
                                </small>

                                <div className="fw-semibold">
                                    {order.StreetAddress}
                                </div>

                                <div className="text-muted small">
                                    {order.transportAddress}
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-6">
                                    <small className="text-muted">
                                        Payment Method
                                    </small>

                                    <div>
                                        <span className="badge bg-dark">
                                            {(order.mop=='CD')?'Cash On Delivery':(order.mop=='OP')?'Online Payment':'N/A'}
                                        </span>
                                    </div>
                                </div>

                                <div className="col-6">
                                    <small className="text-muted">
                                        Transaction
                                    </small>

                                    <div className="small fw-semibold">
                                        {order.TransactionID || "N/A"}
                                    </div>
                                </div>
                            </div>

                            <hr />

                            {/* PAYMENT STATUS */}
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div>
                                    {order.onlinePayment ? (
                                        order.IsTransaction ? (
                                            <span className="badge bg-warning text-dark p-2">
                                                Payment Pending
                                            </span>
                                        ) : (
                                            <span className="badge bg-success p-2">
                                                Paid
                                            </span>
                                        )
                                    ) : (
                                        <span className="badge bg-danger p-2">
                                            Offline Payment
                                        </span>
                                    )}
                                </div>

                                <div className="d-flex gap-2 flex-wrap">
                                    {order.onlinePayment ? (
                                        order.IsTransaction ? (
                                            <button
                                                className="btn btn-primary btn-sm px-3"
                                                onClick={() =>
                                                    orderPayment(order)
                                                }
                                            >
                                                Pay Now
                                            </button>
                                        ) : (
                                            order.VoucherLink && (
                                                <a
                                                    href={order.VoucherLink}
                                                    className="btn btn-success btn-sm px-3"
                                                >
                                                    Download Voucher
                                                </a>
                                            )
                                        )
                                    ) : (
                                        <small className="text-danger">
                                            Contact: {order.hotlineNo}
                                        </small>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    )}
</div>

            <Footer />
        </>
    );
};

export default OrderList;